import React, { useCallback, useEffect, useState } from "react";
import { Hourglass, Timer, Zap, Loader2, Banknote, AlertTriangle } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF, PAYMENT_LABEL, shortTag } from "@/utils/format";
import { toast } from "sonner";

const STATUS_STYLE = {
  waiting: "bg-[#E7F6EE] text-brand-green",
  due: "bg-brand-yellow/25 text-[#946A00]",
  incident: "bg-brand-red/10 text-brand-red",
  missing_account: "bg-brand-red/10 text-brand-red",
  failed_api: "bg-brand-red/10 text-brand-red",
};

/** Admin Finance — upcoming (not yet executed) express payouts, with per-order
 * wait reason, force button, and the cash-orders reminder (no express payout). */
export default function UpcomingPayouts() {
  const [data, setData] = useState(null);
  const [forcing, setForcing] = useState({});

  const load = useCallback(() => {
    apiClient.get("/admin/finance/upcoming-payouts").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // keep countdowns fresh
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;

  const forcePayout = async (orderId) => {
    setForcing((f) => ({ ...f, [orderId]: true }));
    try {
      const { data: res } = await apiClient.post(`/admin/finance/force-payout/${orderId}`);
      if (res.paid?.length) {
        toast.success(`Reversement forcé : ${res.paid.map((p) => `${p.name} ${formatGNF(p.amount)}`).join(" · ")}`);
      }
      if (res.failures?.length) {
        toast.error(`Échec pour : ${res.failures.map((f) => `${f.name} (${f.reason})`).join(" · ")}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setForcing((f) => ({ ...f, [orderId]: false }));
    }
  };

  const nextPass = data.next_pass_at
    ? new Date(data.next_pass_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="space-y-4" data-testid="upcoming-payouts">
      {/* KPI card */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white border border-sand rounded-2xl p-4" data-testid="kpi-upcoming-payouts">
          <div className="w-10 h-10 rounded-full grid place-items-center mb-2 bg-[#FF7900]/15 text-[#FF7900]">
            <Hourglass className="w-4 h-4" />
          </div>
          <div className="text-xs uppercase tracking-widest text-mute">À reverser dans l'heure</div>
          <div className="font-display text-xl sm:text-2xl font-semibold mt-1">{formatGNF(data.kpi.amount)}</div>
          <div className="text-xs text-mute mt-0.5">{data.kpi.orders} commande{data.kpi.orders > 1 ? "s" : ""} en délai H+{data.payout_delay_hours}</div>
        </div>
        <div className="bg-elevated rounded-2xl p-4 flex flex-col justify-center" data-testid="cash-pending-info">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-mute mb-1">
            <Banknote className="w-4 h-4" /> Commandes en espèces (pas de reversement express)
          </div>
          <div className="text-sm">
            <b>{data.cash.orders_count}</b> commande{data.cash.orders_count > 1 ? "s" : ""} cash en attente · part restaurant <b>{formatGNF(data.cash.restaurant_share)}</b>
          </div>
          <div className="text-xs text-mute mt-1">Suivent la compensation de dette livreur + reversement hebdo du lundi.</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-sand rounded-2xl p-5" data-testid="upcoming-payouts-table">
        <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="font-display text-lg font-semibold">Commandes en attente de reversement (paiement en ligne)</h3>
            <p className="text-xs text-mute inline-flex items-center gap-1.5 mt-0.5">
              <Timer className="w-3.5 h-3.5" />
              Délai configuré : <b>H+{data.payout_delay_hours}</b> (PAYOUT_DELAY_HOURS) · Prochain passage du job : <b data-testid="next-pass-at">{nextPass}</b>
            </p>
          </div>
        </div>
        {data.rows.length === 0 ? (
          <div className="text-sm text-mute py-6 text-center" data-testid="upcoming-empty">Aucune commande en ligne en attente de reversement.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-elevated text-mute uppercase text-xs tracking-widest">
                <tr>
                  <th className="p-2 text-left">Commande</th><th className="text-left">Restaurant</th><th className="text-left">Livreur</th>
                  <th>Paiement</th><th className="text-right">Part resto</th><th className="text-right">Part livreur</th>
                  <th className="text-right">Part plateforme</th><th className="text-left pl-3">Statut</th><th></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.order_id} className="border-t border-sand" data-testid={`upcoming-row-${r.order_id}`}>
                    <td className="p-2 font-mono text-xs">#{r.order_id.slice(-6).toUpperCase()}</td>
                    <td className="text-left">{r.restaurant_name || "—"}</td>
                    <td className="text-left">{r.driver_name ? <>{r.driver_name} {r.driver_id && <span className="font-mono text-[10px] text-mute">{shortTag(r.driver_id)}</span>}</> : "—"}</td>
                    <td className="text-center text-xs">{PAYMENT_LABEL[r.payment_method] || r.payment_method}</td>
                    <td className="text-right">{formatGNF(r.part_restaurant)}</td>
                    <td className="text-right">{formatGNF(r.part_livreur)}</td>
                    <td className="text-right text-mute">{formatGNF(r.part_platform)}</td>
                    <td className="pl-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${STATUS_STYLE[r.status] || "bg-elevated text-mute"}`} data-testid={`upcoming-status-${r.order_id}`}>
                        {(r.status === "incident" || r.status === "missing_account" || r.status === "failed_api") && <AlertTriangle className="w-3 h-3" />}
                        {r.status_detail}
                      </span>
                    </td>
                    <td className="text-right pr-1">
                      <button
                        onClick={() => forcePayout(r.order_id)}
                        disabled={forcing[r.order_id] || r.status === "incident"}
                        title={r.status === "incident" ? "Arbitrez l'incident d'abord" : "Ignorer le délai et reverser maintenant"}
                        data-testid={`force-payout-${r.order_id}`}
                        className="inline-flex items-center gap-1 text-xs rounded-full bg-ink hover:bg-black disabled:opacity-40 text-white px-3 py-1.5 font-semibold"
                      >
                        {forcing[r.order_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Forcer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
